import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";

// 콤마가 섞인 문자열 → 순수 숫자. 숫자가 없으면 0.
const parseAmount = (display) => {
  const digits = String(display).replace(/[^\d]/g, "");
  return digits === "" ? 0 : parseInt(digits, 10);
};

// 숫자/문자열 → "1,000,000" 형태. 유효하지 않으면 빈 문자열.
const formatWithComma = (value) => {
  const num = parseAmount(value);
  return num === 0 ? "" : num.toLocaleString();
};

// 관리자 입출금 모달. 회원 목록에서 우클릭 → "관리자 입출금" 클릭 시 노출.
const AdminBalanceModal = ({
  open,
  setOpen,
  targetUser,
  managerId,
  reviseUserBalance,
}) => {
  const [depositDisplay, setDepositDisplay] = useState("");
  const [withdrawalDisplay, setWithdrawalDisplay] = useState("");

  // 모달을 다시 열 때 이전 입력값이 남지 않도록 초기화
  useEffect(() => {
    if (open) {
      setDepositDisplay("");
      setWithdrawalDisplay("");
    }
  }, [open]);

  if (!targetUser) return null;

  const handleExecute = (isDeposit) => {
    const amount = isDeposit
      ? parseAmount(depositDisplay)
      : parseAmount(withdrawalDisplay);

    if (amount < 1) {
      toast.error("0원보다 큰 수를 입력해주세요");
      return;
    }

    reviseUserBalance({
      variables: {
        managerId,
        userId: targetUser.id,
        isDeposit,
        value: amount,
      },
    });

    if (isDeposit) setDepositDisplay("");
    else setWithdrawalDisplay("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>관리자 입출금</DialogTitle>
          <DialogDescription>
            선택한 회원의 담보금을 직접 조정합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 아이디 / 이름 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>아이디</Label>
              <div className="flex items-center h-9 px-3 border rounded-md bg-muted text-sm">
                {targetUser.id}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>이름</Label>
              <div className="flex items-center h-9 px-3 border rounded-md bg-muted text-sm">
                {targetUser.userName ?? "-"}
              </div>
            </div>
          </div>

          {/* 현재 잔고 */}
          <div className="space-y-1.5">
            <Label>현재 잔고</Label>
            <div className="flex items-center gap-1 h-9 px-3 border rounded-md bg-muted text-sm">
              <span>&#8361;</span>
              <span>{(targetUser.userBalance ?? 0).toLocaleString()}</span>
            </div>
          </div>

          {/* 입금 */}
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label>입금 금액</Label>
              <Input
                inputMode="numeric"
                placeholder="0"
                value={depositDisplay}
                onChange={(e) =>
                  setDepositDisplay(formatWithComma(e.target.value))
                }
              />
            </div>
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => handleExecute(true)}
            >
              입금
            </Button>
          </div>

          {/* 출금 */}
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label>출금 금액</Label>
              <Input
                inputMode="numeric"
                placeholder="0"
                value={withdrawalDisplay}
                onChange={(e) =>
                  setWithdrawalDisplay(formatWithComma(e.target.value))
                }
              />
            </div>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => handleExecute(false)}
            >
              출금
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBalanceModal;
